import type { PropsWithChildren } from "react";
import { createContext, useMemo } from "react";
import type { ApiError } from "@entrilia/shared/api/types";
import type { Contact, ContactPortalAccess } from "../../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../../context/useClientContext";
import type { ContactEmailEditMode } from "../contactEmailEditModel";
import { resolveContactEmailEditMode } from "../contactEmailEditModel";

interface ContextValue {
  portalAccess?: ContactPortalAccess;
  error?: ApiError;
  isLoading: boolean;
  refetch: () => void;
  emailEditMode: ContactEmailEditMode;
}

const PortalAccessContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  contact: Contact;
  portalAccess?: ContactPortalAccess;
  error?: ApiError;
  isLoading: boolean;
  onRefetch: () => void;
}

export const PortalAccessContextProvider = ({
  contact,
  portalAccess,
  error,
  isLoading,
  onRefetch,
  children,
}: PropsWithChildren<Props>) => {
  const { investorPortalApp } = useClientContext();

  const contextValue = useMemo(
    () => ({
      portalAccess,
      error,
      isLoading,
      refetch: onRefetch,
      emailEditMode: resolveContactEmailEditMode({
        portalLogin: contact.investorPortalLogin,
        hasPortalApp: Boolean(investorPortalApp),
        plan: portalAccess?.emailChange,
        isPortalAccessLoading: isLoading,
      }),
    }),
    [contact.investorPortalLogin, investorPortalApp, portalAccess, error, isLoading, onRefetch]
  );

  return <PortalAccessContext value={contextValue}>{children}</PortalAccessContext>;
};

export default PortalAccessContext;
